<template>
    <div>
        <report-header
            :title="title"
            :isShowDateRange="true"
            :formatDateRangeTitle="dateRange"
        />
        <b-card id="report-card">
            <div id="report-body" class="w-100">
                <div v-if="isFirstLoad || result.length == 0">
                    <p class="item-name" v-if="isFirstLoad">
                        Loading cash..
                    </p>
                    <QTable
                        :columns="defaultLoadingColumns"
                        :data="[]"
                        hide-bottom
                        :loading="isLoading"
                        :rows-per-page-options="[0]"
                    >
                        <template
                            v-slot:bottom-row
                            v-if="
                                result.length == 0 &&
                                !isLoading &&
                                !isError &&
                                !isFirstLoad
                            "
                        >
                            <q-tr>
                                <q-td colspan="8">No records available</q-td>
                            </q-tr>
                        </template>
                    </QTable>
                </div>
                <div
                    :id="'table_' + item.key"
                    v-else
                    v-for="(item, index) in result"
                    :key="index"
                >
                    <p class="item-name">
                        {{ _setDateDisplay(Number(item.unixtime)) }}
                    </p>
                    <QTable
                        :columns="defaultLoadingColumns"
                        :data="properRecords(item.data)"
                        hide-bottom
                        :loading="isLoading"
                        :rows-per-page-options="[0]"
                    >
                        <template v-slot:header-cell="props">
                            <q-th :props="props">
                                <strong>{{ props.col.label }}</strong>
                            </q-th>
                        </template>
                        <template v-slot:body-cell-amount="props">
                            <q-td :props="props" v-if="props.row.amount === ''">
                                {{ props.row.amount }}
                            </q-td>
                            <q-td :props="props" v-else>
                                {{ props.value }}
                            </q-td>
                        </template>
                        <template v-slot:body-cell-time="props">
                            <q-td :props="props">
                                <strong v-if="props.row.isBold">{{
                                    props.value
                                }}</strong>
                                <span v-else>{{ props.value }}</span>
                            </q-td>
                        </template>
                        <template v-slot:body-cell-balance="props">
                            <q-td :props="props">
                                <strong v-if="props.row.isBold">{{
                                    props.value
                                }}</strong>
                                <span v-else>{{ props.value }}</span>
                            </q-td>
                        </template>
                        <template v-slot:body-cell-posted_by="props">
                            <q-td :props="props">
                                <a
                                    class="plan-text"
                                    :href="
                                        '../apps/business/staff/view/' +
                                        props.row.posted_by_encoded_id
                                    "
                                >
                                    {{ props.row.posted_by }}
                                </a>
                            </q-td>
                        </template>
                        <template v-slot:body-cell-transaction="props">
                            <q-td :props="props">
                                <strong v-if="props.row.isBold">
                                    {{
                                        props.row.transaction.replace(
                                            props.row.client_name,
                                            ""
                                        )
                                    }}</strong
                                >
                                <span v-else>
                                    {{
                                        props.row.transaction.replace(
                                            props.row.client_name,
                                            ""
                                        )
                                    }}
                                </span>

                                <a
                                    v-if="
                                        USE_CLIENT_MODULE_VUE &&
                                        props.row.client_name &&
                                        !props.row.client_name.includes(
                                            '(deleted)'
                                        )
                                    "
                                    :href="`/view/client/profile/${props.row.client_encoded_id}`"
                                >
                                    {{ props.row.client_name }}
                                </a>
                                <a
                                    class="plain-text"
                                    v-else-if="
                                        !USE_CLIENT_MODULE_VUE &&
                                        props.row.client_name &&
                                        !props.row.client_name.includes(
                                            '(deleted)'
                                        )
                                    "
                                    :href="
                                        '../apps/client/client/view/' +
                                        props.row.client_encoded_id
                                    "
                                >
                                    {{ props.row.client_name }}
                                </a>
                                <span v-else-if="props.row.client_name">
                                    {{ props.row.client_name }}
                                </span>
                            </q-td>
                        </template>
                    </QTable>
                </div>
            </div>
        </b-card>
    </div>
</template>

<script>
import { QTable } from "quasar";
import Report from "@/mixins/report.js";
import ReportService from "@/services/report";
import CREPORT from "@/constants/report";
import "@/css/quasar-table.css";
export default {
    mixins: [Report],
    components: {
        QTable,
        ReportHeader: () => import("@/components/report/report-header")
    },
    props: {
        data: {
            type: Object,
            default() {
                return {
                    start: this.getStartDate("day"),
                    end: this.getStartDate("day")
                };
            }
        }
    },
    data() {
        return {
            locale: "en-US",
            loaded: false,
            meta: "",
            result: [],
            tableHeader: [],
            title: "Cash Reconciliation",
            dateRange: "",
            USE_CLIENT_MODULE_VUE: process.env.USE_CLIENT_MODULE_VUE === "true",
            isFirstLoad: true,
            isLoading: false,
            isError: false
        };
    },
    mounted() {
        let self = this;
        // --- toggle navigation options
        self.data.filterDateOption = true;
        self.data.isShowDateRange = false;
        self.data.isShowDateTimeRange = true;
        self.data.datePickerOptionDefault = 1;

        self._getSetting().then(() => {
            self.data.start = self.getStartDate("day");
            self.data.end = self.getEndDate("day");
            self.data.from = self.converToUnix(self.data.start);
            self.data.to = self.converToUnix(self.data.end);
            self.dynamicPageTitle(self.title);
            self.init();
        });

        self.$root.$on("report-filter-date-range:refresh", self.init);
        window.scrollTo(0, 0);
    },
    beforeDestroy: function () {
        this.data.datePickerOptionDefault = null;
        this.data.isShowDateTimeRange = null;
        this.$root.$off("report-filter-date-range:refresh", this.init);
    },
    methods: {
        async init(params) {
            let self = this;
            self.loaded = false;
            self.isLoading = true;
            self.isError = false;

            if (params) {
                self.data.from = params.from;
                self.data.to = params.to;
            }

            self.dateRange = self.getDateRange(self.data.from, self.data.to);

            await ReportService.getReport(CREPORT.TYPE.CASH_RECONCILIATION, {
                from: self.data.from,
                to: self.data.to
            })
                .then(res => {
                    if (res && res.message && res.message.code === 200) {
                        self.meta = res.resource.meta;
                        self.locale = self.meta.locale.replace("_", "-");
                        self.result = self.generateResultArray(
                            res.resource.results
                        );

                        self.tableHeader = res.resource.headers;

                        self.title = res.resource.meta.title;
                        self.dynamicPageTitle(self.title);
                        self.dateRange = self.getDateRange(
                            res.resource.meta.from,
                            res.resource.meta.to
                        );
                        self.loaded = true;
                    } else {
                        throw new Error("Error: Unable to get the report.");
                    }
                })
                .catch(e => {
                    console.error(e.message);
                    self.isError = true;
                    self._message("Error: Unable to get the report", "danger");
                })
                .finally(() => {
                    self.isLoading = false;
                    self.isFirstLoad = false;
                });
        },
        properRecords(records = {}) {
            let res = [];
            let { adjustment, end, start, ...data } = records;

            res.push({ ...start, isBold: true });

            Object.keys(data)
                .sort()
                .forEach(key => {
                    res.push(data[key]);
                });

            if (adjustment) {
                res.push({ ...adjustment, isBold: false });
            }

            res.push({ ...end, isBold: true });
            return res;
        },
        generateResultArray(rawResult = {}) {
            let result = [];

            for (let unixtime in rawResult) {
                let data = { ...rawResult[unixtime] };

                if (Object.keys(data).length === 2 && data.start && data.end) {
                    continue; // skip table display if there's only 'start' and 'end'
                }

                result.push({
                    unixtime: unixtime,
                    data: data
                });
            }

            return result.sort((a, b) => b.unixtime - a.unixtime); // ensure sort by desc
        }
    },
    computed: {
        defaultLoadingColumns() {
            let columns = [
                {
                    name: "time",
                    label: "Time",
                    field: "time",
                    align: "left",
                    style: "white-space: nowrap; width: 120px;"
                },
                {
                    name: "transaction",
                    label: "Transaction",
                    field: "transaction",
                    align: "left",
                    style: "white-space: nowrap"
                },
                {
                    name: "posted_by",
                    label: "Posted By",
                    field: "posted_by",
                    align: "left",
                    style: "white-space: nowrap; width: 120px;"
                },
                {
                    name: "amount",
                    label: "Amount",
                    field: "amount",
                    format: (val, row) => {
                        return this._toCurrency(row.amount);
                    },
                    align: "left",
                    style: "white-space: nowrap; width: 120px;"
                },
                {
                    name: "balance",
                    label: "Balance",
                    field: "balance",
                    format: (val, row) => {
                        return this._toCurrency(row.balance);
                    },
                    align: "left",
                    style: "white-space: nowrap; width: 120px;"
                }
            ];

            return columns;
        }
    }
};
</script>
<style>
.total tr:last-child {
    font-weight: bold;
}
</style>
